import classnames from 'classnames'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStar } from '@fortawesome/pro-solid-svg-icons'

import {
  faAngleRight, faBoxAlt, faCog, faGlobe, faLink,
} from '@fortawesome/pro-light-svg-icons'

import {
  Box, IconWithMenu, Image, Link, Text,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { confirmAction } from '@functions/entity'

import useCampaign, { generateUrls } from '@hooks/useCampaign'

import type { CampaignModel, UserModel } from '@models/types'

import { getCampaignPermissions, getCampaignType } from '@functions/campaign'

import placeholderImageUrl from '../assets/campaign_image_400x400.png'

import StatusIcon from './StatusIcon'
import styles from './styles.module.scss'

interface TileProps {
  campaign: CampaignModel,
  currentUser: UserModel,
}

const Tile = (props: TileProps) => {
  const {
    campaign,
    campaign: {
      address, default_image, priority_campaign_user_ids: priorityUserIds, published, suburb_name,
    },
    currentUser,
  } = props

  const portal = digObject(campaign, 'data.portal')

  const {
    callbacks: {
      toggleHidden,
    },
  } = useCampaign(campaign)

  const { campaignEditDashboardUrl, campaignQuotesUrl } = generateUrls(campaign)

  const campaignPermissions = getCampaignPermissions({
    user: currentUser,
    campaignType: getCampaignType(campaign),
  })
  const canManageCampaign = campaignPermissions?.orders.manage ?? false
  const editUrl = canManageCampaign ? campaignEditDashboardUrl : campaignQuotesUrl

  const isPriorityCampaignForCurrentUser = priorityUserIds?.includes(currentUser.id)

  const confirmActionPayload = {
    callbacks: {
      actionFn: toggleHidden,
    },
    options: {
      confirmButtonColor: '#DD6B55',
      icon: 'warning',
    },
    strings: {
      actionString: 'archive',
      entityName: 'campaign',
    },
  }

  return (
    <Image
      borderRadius={5}
      className={
        default_image ? styles.root : classnames(styles.root, styles.noImage)
      }
      placeholderUrl={placeholderImageUrl}
      url={default_image}
    >
      <span className={styles.gradient} />
      <a href={editUrl} className={styles.linkOverlay} />

      <Box className={styles.icons} padding="medium">
        {published && (<StatusIcon icon={faGlobe} hint="Campaign is currently Published" />)}

        {portal && (
          <StatusIcon icon={faLink} hint={`Campaign is syncing from ${portal.title || portal}`} />
        )}

        <IconWithMenu
          icon={(
            <Link greyLink>
              <StatusIcon icon={faCog} />
            </Link>
          )}
        >
          {/*
           // @ts-expect-error TS2741 */}
          <IconWithMenu.Menu closeOnClick>
            {/*
             // @ts-expect-error TS2345 */}
            <Link onClick={() => confirmAction(confirmActionPayload)}>
              <FontAwesomeIcon icon={faBoxAlt} /> Archive Campaign
            </Link>
          </IconWithMenu.Menu>
        </IconWithMenu>
      </Box>

      <Box className={styles.priorityCampaignIcon} padding="medium">
        {isPriorityCampaignForCurrentUser && (<FontAwesomeIcon icon={faStar} />)}
      </Box>

      <Box
        className={styles.details}
        flexDirection="column"
        padding="large"
        position="relative"
      >
        <Text className={styles.suburb} marginBottom="medium">
          {suburb_name}
        </Text>

        <Text className={styles.address} fontSize="medium">
          {address}
        </Text>

        <FontAwesomeIcon className={styles.angleRight} icon={faAngleRight} />
      </Box>
    </Image>
  )
}

export default Tile
